import { forms } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * The Forms screens' decisions, out of the components so they can be asserted
 * without mounting anything.
 *
 * THE THREE READINGS THAT CARRY THE DOMAIN'S POINT.
 *
 *   AUTHORSHIP — `authoredBy: null` on an `ai_drafted` version is a CLAIM ("no
 *     person wrote this"), not a missing join. {@link authorshipReading} keeps
 *     that apart from a human version whose author could not be resolved.
 *   APPROVAL — a version is publishable only when a person approved THIS
 *     content hash. {@link publishability} says which of the four reasons a
 *     version is not, so a screen never offers a button the server will refuse.
 *   INERT — an unapproved signal mapping matches and emits NOTHING.
 *     {@link mappingReading} says so in words, because `active: true` beside a
 *     null approval reads as "on" and is not.
 */

// ------------------------------------------------------------------ readings

export const VERSION_STATE_READING: Record<
  forms.FormVersionState,
  { variant: Severity; label: string; meaning: string }
> = {
  draft: {
    variant: 'plain',
    label: 'Draft',
    meaning: 'Being written. Nobody has been asked to look at it.',
  },
  review: {
    variant: 'look',
    label: 'In review',
    meaning: 'Waiting for a person to approve or reject it.',
  },
  approved: {
    variant: 'sky',
    label: 'Approved',
    meaning:
      'A person approved this exact content. It is not in use yet — publishing is a separate step.',
  },
  published: {
    variant: 'good',
    label: 'Published',
    meaning: 'In use. Responses answer this version and keep pointing at it afterwards.',
  },
  retired: {
    variant: 'plain',
    label: 'Retired',
    meaning: 'No longer in use. Responses already recorded against it are unaffected.',
  },
};

export const INSTRUMENT_READING: Record<forms.FormInstrumentKind, string> = {
  assessment: 'Assessment',
  screening: 'Screening',
  check_in: 'Check-in',
  incident: 'Incident',
  survey: 'Survey',
  audit: 'Audit',
  checklist: 'Checklist',
  onboarding: 'Onboarding',
  custom: 'Custom',
};

export const SUBJECT_READING: Record<forms.FormSubjectKind, string> = {
  client: 'A client',
  staff: 'A staff member',
  program: 'A program',
  location: 'A location',
  task: 'A task',
};

export const CADENCE_READING: Record<forms.FormCadence, string> = {
  none: 'No repeat',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

export const DECISION_READING: Record<
  forms.FormApprovalDecision,
  { variant: Severity; label: string }
> = {
  approved: { variant: 'good', label: 'Approved' },
  rejected: { variant: 'urgent', label: 'Rejected' },
  changes_requested: { variant: 'look', label: 'Changes requested' },
};

/**
 * The history's event kinds — with a FALLBACK, unlike every other reading here.
 *
 * `FormEventView.kind` is typed `string` on the wire, not a union, so a `Record`
 * over a closed list would be a lie about what can arrive. The known keys get
 * their sentence; anything else is shown as itself rather than as a wrong label.
 */
const EVENT_READING: Record<string, string> = {
  definition_created: 'Form created',
  version_drafted: 'Version drafted',
  version_edited: 'Version edited',
  submitted_for_review: 'Sent for review',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Changes requested',
  published: 'Published',
  retired: 'Retired',
  assigned: 'Assigned',
  mapping_proposed: 'Signal mapping proposed',
  mapping_approved: 'Signal mapping approved',
  response_started: 'Response started',
  response_submitted: 'Response submitted',
  signal_emitted: 'Signal emitted',
};

export function eventReading(kind: string): string {
  return EVENT_READING[kind] ?? kind;
}

// ---------------------------------------------------------------- authorship

/**
 * Who wrote this version, at the resolution the domain cares about.
 *
 *   'person'      — a human wrote it, and here they are.
 *   'ai'          — no person wrote it. `authoredBy` is null BY CONSTRUCTION,
 *                   and that null is the claim.
 *   'unresolved'  — `authorship: 'human'` with no author. Not a state the server
 *                   produces; if it ever arrives, it is a missing join and must
 *                   NOT be presented as "written by AI".
 *
 * The third arm is the whole reason this is a function. Collapsing it into 'ai'
 * would libel a person; collapsing 'ai' into it would erase the disclosure the
 * approval flow exists to force.
 */
export type AuthorshipReading =
  | { kind: 'person'; name: string; assisted: boolean; modelRef: string | null }
  | { kind: 'ai'; modelRef: string | null; assisted: boolean }
  | { kind: 'unresolved' };

export function authorshipReading(version: forms.FormVersionView): AuthorshipReading {
  if (version.authorship === 'human') {
    return version.authoredBy === null
      ? { kind: 'unresolved' }
      : {
          kind: 'person',
          name: version.authoredBy.displayName,
          assisted: false,
          modelRef: null,
        };
  }
  const assisted = version.authorship === 'ai_assisted';
  // `ai_assisted` still records no human author on the server, so there is no
  // person to name here either. What differs is what the approver is being
  // asked to attest to, and the sentence says which.
  return { kind: 'ai', modelRef: version.aiModelRef, assisted };
}

export function authorshipSentence(reading: AuthorshipReading): string {
  switch (reading.kind) {
    case 'person':
      return `Written by ${reading.name}.`;
    case 'ai':
      return reading.assisted
        ? `Drafted with model assistance${reading.modelRef ? ` (${reading.modelRef})` : ''}. No person is recorded as its author — approving it means attesting that you reviewed what the model produced.`
        : `Written by a model${reading.modelRef ? ` (${reading.modelRef})` : ''}, not by a person. Approving it means attesting that you reviewed what the model produced.`;
    case 'unresolved':
      return 'This version is recorded as human-authored, but we could not resolve who wrote it. That is a gap in the record, not a claim that nobody did.';
  }
}

/** Does approving this version require an account of reviewing model output? */
export function needsAiReviewStatement(version: forms.FormVersionView): boolean {
  return version.authorship !== 'human';
}

// -------------------------------------------------------------- publishing

/**
 * Why this version cannot be published, or that it can.
 *
 * The server's gate is structural — `publish` takes no body and the database
 * refuses a published version with no approval bound to its content hash — so
 * this is a COURTESY, never the boundary. Its job is to stop a screen offering
 * a button whose only outcome is a 409 the reader cannot act on.
 */
export type Publishability =
  | { kind: 'publishable' }
  | { kind: 'already-published' }
  | { kind: 'retired' }
  | { kind: 'needs-approval'; state: forms.FormVersionState };

export function publishability(version: forms.FormVersionView): Publishability {
  if (version.state === 'published') return { kind: 'already-published' };
  if (version.state === 'retired') return { kind: 'retired' };
  // THE APPROVAL, NOT THE STATE WORD. `state: 'approved'` and `approvalId: null`
  // is the shape the publication gate exists to refuse, and reading the state
  // alone would offer the button for exactly that row.
  if (version.approvalId === null || version.state !== 'approved') {
    return { kind: 'needs-approval', state: version.state };
  }
  return { kind: 'publishable' };
}

export function publishabilitySentence(p: Publishability): string {
  switch (p.kind) {
    case 'publishable':
      return 'A person has approved this exact content, so it can be published.';
    case 'already-published':
      return 'Already in use.';
    case 'retired':
      return 'Retired. Publish a new version instead.';
    case 'needs-approval':
      return p.state === 'draft'
        ? 'Still a draft. Send it for review first — nothing publishes that a person has not approved.'
        : 'No approval is bound to this exact content yet. Nothing publishes that a person has not approved, and there is no way to override that from this screen.';
  }
}

// ----------------------------------------------------------------- mappings

/**
 * What a signal mapping actually does right now.
 *
 * `active: true` with a null approval reads as "on" and is not: an unapproved
 * mapping matches and emits nothing at all. Two fields, one sentence, so a
 * screen cannot show half of it.
 */
export type MappingReading =
  { kind: 'live'; raisesTask: boolean } | { kind: 'unapproved' } | { kind: 'retired' };

export function mappingReading(mapping: forms.FormSignalMappingView): MappingReading {
  if (!mapping.active) return { kind: 'retired' };
  if (mapping.mappingApprovalId === null) return { kind: 'unapproved' };
  return { kind: 'live', raisesTask: mapping.automation === 'raise_task' };
}

export function mappingSentence(reading: MappingReading): string {
  switch (reading.kind) {
    case 'live':
      return reading.raisesTask
        ? 'Live — a matching answer raises a task.'
        : 'Live — a matching answer records a signal, and raises nothing.';
    case 'unapproved':
      return 'Proposed and INERT. A matching answer emits nothing until somebody approves this mapping — the silence is a decision, not a fault.';
    case 'retired':
      return 'Retired. It no longer matches anything.';
  }
}

export function mappingPill(reading: MappingReading): { variant: Severity; label: string } {
  switch (reading.kind) {
    case 'live':
      return { variant: 'good', label: 'Live' };
    case 'unapproved':
      return { variant: 'look', label: 'Inert' };
    case 'retired':
      return { variant: 'plain', label: 'Retired' };
  }
}

// ------------------------------------------------------------------ summary

/**
 * The version a reader means when they say "this form".
 *
 * The PUBLISHED one, and only that — highest version number among published
 * rows. `undefined` when nothing is published, which is a real and common state
 * for a form still being written, and must not be substituted with the newest
 * draft: showing a draft where a reader expects the live instrument is how a
 * question nobody approved gets asked of a client.
 */
export function publishedVersion(
  versions: readonly forms.FormVersionView[],
): forms.FormVersionView | undefined {
  return versions
    .filter((v) => v.state === 'published')
    .sort((a, b) => b.versionNumber - a.versionNumber)[0];
}

/** Versions waiting on a person, newest first. */
export function awaitingReview(
  versions: readonly forms.FormVersionView[],
): forms.FormVersionView[] {
  return versions
    .filter((v) => v.state === 'review')
    .sort((a, b) => b.versionNumber - a.versionNumber);
}

/**
 * The one-line state of a definition in the list.
 *
 * FOUR STATES, and the distinction that matters is the last two: "in use" and
 * "nothing published yet" are different facts, and a form that is retired at the
 * DEFINITION level is different again from one whose only version is retired.
 */
export type DefinitionReading =
  | { kind: 'in-use'; versionNumber: number }
  | { kind: 'awaiting-review'; count: number }
  | { kind: 'draft-only' }
  | { kind: 'inactive' };

export function definitionReading(
  definition: forms.FormDefinitionView,
  versions: readonly forms.FormVersionView[] | undefined,
): DefinitionReading | { kind: 'unknown' } {
  if (!definition.active) return { kind: 'inactive' };
  // NOT ASKED is its own answer. The list endpoint does not carry versions, so a
  // screen that has only the definition must say it does not know rather than
  // print "nothing published yet" about a form that may well be in use.
  if (versions === undefined) return { kind: 'unknown' };
  const live = publishedVersion(versions);
  if (live) return { kind: 'in-use', versionNumber: live.versionNumber };
  const waiting = awaitingReview(versions);
  if (waiting.length > 0) return { kind: 'awaiting-review', count: waiting.length };
  return { kind: 'draft-only' };
}

// ------------------------------------------------------------------- format

export function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The server's sentence, plus its per-issue list when it sent one.
 *
 * `forms.formIssues` reads BOTH 400 shapes this domain produces — a Zod failure
 * and a `FormValidationError` saying how a submission does not fit the version it
 * answers. A screen showing only `message` drops every one of them, and they are
 * the half that says what to change.
 */
export function messageOf(err: unknown): string {
  const issues = forms.formIssues(err);
  let head = 'That did not save. Try again.';
  if (typeof err === 'object' && err !== null) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') head = message.trim();
  }
  return issues.length === 0 ? head : `${head} ${issues.join(' · ')}`;
}
